/**
 * §E/§F chunk verify list (restored from fold-in park 55822ae6f). Moved out of
 * BrandBiblePage.tsx (3,000-line rule) with no behaviour change.
 */
import { useState } from 'react';
import { message } from 'antd';
import { VerifiableRecordList, type VerifiableRecord, type VerifyAllConfig } from '@/components/brand-bible/collection';
import { type BbEntry, type ChunkKind } from '@/features/brand-bible-wizard/types';
import { EnvelopeBadge } from '@/components/brand-bible-wizard/primitives';

// ---------------------------------------------------------------------------
// §E Market Notes — chunk verify list (restored from fold-in park 55822ae6f
// for §E Market Notes (recover/bb-e-foldin)). Read/verify surface for
// bb_chunks rows (icp_note/seasonality_note) that otherwise have no UI in §E.
// Thin adapter over the shared VerifiableRecordList primitive. §E-only 'onEditBody'
// opt-in makes rows show the full body with inline edit (design '1b — by the book').
// ---------------------------------------------------------------------------

const CHUNK_KIND_LABEL: Partial<Record<ChunkKind, string>> = {
  icp_note: 'ICP note',
  seasonality_note: 'Seasonality',
  market_narrative: 'Market narrative',
  competitor: 'Competitor',
};

function truncateBody(text: string, max = 160): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

/**
 * §E/§F chunk record → VerifiableRecord adapter (N7 fold-in). The primitive
 * owns the verified/pending badge and the Verify/Unverify/Verify-all chrome;
 * this only adds the kind badge, source/low-confidence badges, and (§F only,
 * via `showInternalBadge`) the internal-only badge on top of it.
 */
interface ChunkVerifyRecord extends VerifiableRecord {
  kind: ChunkKind | undefined;
  publicMention?: boolean;
  /** Full, trimmed body — `label` stays truncated for the read view; the
   * inline editor (§E "1b — by the book") seeds and renders from this. */
  body: string;
}

function toChunkVerifyRecord(entry: BbEntry<'chunks'>): ChunkVerifyRecord {
  return {
    id: entry.id,
    label: truncateBody(entry.body ?? ''),
    verified: Boolean(entry.verified_by),
    verifiedAt: entry.verified_at ?? undefined,
    source: entry.source,
    confidence: entry.confidence,
    kind: entry.kind,
    publicMention: entry.public_mention,
    body: (entry.body ?? '').trim(),
  };
}

/**
 * §E/§F compact chunk list body (N7 fold-in — was a bespoke <ul> + local
 * VerifyAllButton pair; now a thin adapter over VerifiableRecordList). No
 * outer card chrome — callers decide whether this sits inside an existing
 * card (§F, below the border it already draws for its legacy-fields divider
 * elsewhere) or gets its own (§E, since SectionAudience is a separate
 * component this file doesn't own). Renders nothing when `entries` is empty;
 * callers should skip the wrapper too in that case so no empty divider/card
 * appears.
 */
export function ChunkVerifyListBody({
  title,
  entries,
  canVerify,
  onVerify,
  onUnverify,
  onEditBody,
  showInternalBadge = false,
  verifyAll,
}: {
  title: string;
  entries: BbEntry<'chunks'>[];
  canVerify: boolean;
  onVerify: (id: string) => Promise<void>;
  onUnverify: (id: string) => Promise<void>;
  /** §E-only ("1b — by the book") — when provided, rows show the FULL body and
   * gain an inline ✎ Edit affordance; absent, the list is byte-identical to
   * before (truncated label, no editor). */
  onEditBody?: (id: string, body: string) => Promise<void>;
  /** §F-only — competitor rows created with public_mention:false get a quiet badge. */
  showInternalBadge?: boolean;
  verifyAll: VerifyAllConfig;
}) {
  const editable = Boolean(onEditBody);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  if (entries.length === 0) return null;
  const records = entries.map(toChunkVerifyRecord);

  const startEdit = (r: ChunkVerifyRecord) => {
    setEditingId(r.id);
    setDraft(r.body);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };
  const saveEdit = async (r: ChunkVerifyRecord) => {
    const trimmed = draft.trim();
    // Empty or unchanged → nothing to assert; just close (no PATCH, no verify).
    if (!trimmed || trimmed === r.body) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onEditBody!(r.id, trimmed);
      setSaving(false);
      cancelEdit();
    } catch {
      // Keep the editor open so the operator's text isn't lost.
      setSaving(false);
      message.error("Couldn't save note — try again");
    }
  };

  return (
    <>
      <div className="mb-2">
        <span className="text-[11px] tracking-wider text-muted-foreground uppercase font-semibold">
          {title}
        </span>
      </div>
      <VerifiableRecordList
        records={records}
        canVerify={canVerify}
        onVerify={(r) => onVerify(r.id)}
        onUnverify={(r) => onUnverify(r.id)}
        verifyAll={verifyAll}
        label={title}
        renderLabel={(r) => (
          <>
            <span className="flex-none rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-teal">
              {(r.kind && CHUNK_KIND_LABEL[r.kind]) || r.kind}
            </span>
            <span className="text-[13px] leading-[19px] text-foreground">
              {editable ? r.body : r.label}
            </span>
          </>
        )}
        renderBadges={(r) => (
          <>
            <EnvelopeBadge kind="source">{r.source}</EnvelopeBadge>
            {r.confidence === 'low' && <EnvelopeBadge kind="low">low confidence</EnvelopeBadge>}
            {showInternalBadge && r.kind === 'competitor' && r.publicMention === false && (
              <span className="rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-teal">
                internal only
              </span>
            )}
            {editable && canVerify && editingId !== r.id && (
              <button
                type="button"
                onClick={() => startEdit(r)}
                className="text-[11.5px] font-medium text-muted-foreground hover:text-coral disabled:cursor-not-allowed disabled:opacity-60"
              >
                ✎ Edit
              </button>
            )}
          </>
        )}
        renderDetails={
          editable
            ? (r) =>
                editingId === r.id ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <textarea
                      autoFocus
                      rows={4}
                      aria-label="Edit note"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      className="w-full rounded-md border border-border bg-card px-2.5 py-1.5 text-[13px] leading-[19px] text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void saveEdit(r)}
                        className="inline-flex items-center rounded-md bg-coral px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-coral-hover disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {saving ? 'Saving…' : 'Save note'}
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={cancelEdit}
                        className="text-[11.5px] font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      <span className="text-[11px] text-muted-foreground">
                        Saving marks this verified by you
                      </span>
                    </div>
                  </div>
                ) : null
            : undefined
        }
      />
    </>
  );
}
